import { useEffect, useState } from "react";
import { useSpring, animated as a } from "react-spring";
import { styled, useTheme } from "@mui/material";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import SearchIcon from "@mui/icons-material/Search";
import clsx from "clsx";
import Title from "components/Typography/Title";
import "../../assets/styles/animation.css";

const TitleStyled = styled(Title)(({ theme }) => ({
  color: theme.color.sidebarTitle,
  display: "flex",
  justifyContent: "space-between",
  padding: "1rem 1.5rem 1.5rem",
  fontSize: "1.5rem",
  fontWeight: 700,
}));

const ContainerStyled = styled(a.div)(({ theme }) => ({
  zIndex: theme.zIndex.sidebar,
  height: "100%",
}));

const InnerStyled = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  width: `calc(${theme.width.sidebar} - 1rem)`,
  height: "calc(100% - 1rem)",
  margin: "0.5rem",
  paddingTop: "16px",
  backgroundColor: theme.color.white100,
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: "16px",
  boxSizing: "border-box",
  overflow: "hidden",
  "&.large": {
    width: `calc(${theme.width.drawer} - 1rem)`,
  },
  "&.right": {
    boxShadow: theme.boxShadow.left,
  },
  "& .toggle-btn": {
    background: "#07418b",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    verticalAlign: "top",
    border: "none",
    position: "absolute",
    marginLeft: "19rem",
    marginTop: "4rem",
    cursor: "pointer",
    height: "2.5rem",
    width: "1.5rem",
    borderRadius: "0 1rem 1rem 0",
    zIndex: 999999999,
    "& > *": {
      color: "#fff",
    },
  },
}));

export const SidebarContainer = ({
  children,
  className,
  classNames = {}, // wrapper, inner
  shouldAllowSideBarToggle = false,
  onOpen,
  isOpen,
  side = "left",
  size = "md",
}) => {
  const theme = useTheme();

  let width = theme.width.sidebar;
  if (size === "lg") {
    width = theme.width.drawer;
  }

  const [isSidebarOpen, setIsSidebarOpen] = useState(
    typeof isOpen !== "undefined" ? isOpen : true
  );

  const toggleDrawer = () => {
    if (typeof onOpen === "function") {
      onOpen(!isSidebarOpen);
    } else {
      setIsSidebarOpen((prev) => !prev);
    }
  };

  useEffect(() => {
    if (typeof isOpen !== "undefined") {
      setIsSidebarOpen(isOpen);
    }
  }, [isOpen]);

  const animated = useSpring({
    from: {
      ...(side === "left"
        ? { marginLeft: isSidebarOpen ? 0 : `-${width}` }
        : { marginRight: isSidebarOpen ? 0 : `-${width}` }),
      opacity: 1,
    },
    to: {
      ...(side === "left"
        ? { marginLeft: isSidebarOpen ? 0 : `-${width}` }
        : { marginRight: isSidebarOpen ? 0 : `-${width}` }),
      opacity: 1,
    },
    config: {
      mass: 1,
      tension: 700,
      friction: 50,
    },
  });

  return (
    <ContainerStyled
      style={animated}
      className={clsx(
        "wrapper camvio-smooth-animated-3",
        className,
        classNames.wrapper
      )}
    >
      <InnerStyled
        className={clsx(
          "inner",
          { large: size === "lg" },
          side,
          classNames.inner
        )}
      >
        {children}
        {shouldAllowSideBarToggle && (
          <button className="toggle-btn" onClick={toggleDrawer}>
            {isSidebarOpen ? (
              <KeyboardArrowLeftIcon fontSize="medium" />
            ) : (
              <SearchIcon fontSize="medium" />
            )}
          </button>
        )}
      </InnerStyled>
    </ContainerStyled>
  );
};

SidebarContainer.Title = TitleStyled;
export default SidebarContainer;
